import React, { useState, useEffect } from 'react';
import "./NewQuery.css";
import { getDatabase, ref, push, get, set } from 'firebase/database';
import { ref as dbRef, onValue } from "firebase/database";
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { FaInfoCircle, FaPaperPlane, FaClock, FaCheckCircle, FaTimesCircle, FaPhone } from 'react-icons/fa';
import { Link } from 'react-router-dom';

const gifUrl = "/Images/New notification.gif";

// Categories configuration - centralized for easy maintenance
const CATEGORIES = {
  CEMENT: 'Cement',
  STEEL: 'Steel',
  MACHINERY: 'Machinery',
  FINANCE: 'Finance'
};

// Field configurations for each category
const categoryFields = {
  [CATEGORIES.CEMENT]: {
    options: {
      brand: [
        { value: 'Ultratech', label: 'Ultratech' },
        { value: 'Shree', label: 'Shree' },
        { value: 'JSW', label: 'JSW' },
        { value: 'ACC', label: 'ACC' },
        { value: 'JKSuper', label: 'JK Super' },
        { value: 'Ambuja', label: 'Ambuja' },
        { value: 'All', label: 'All Brands' }
      ],
      grade: [
        { value: 'OPC43', label: 'OPC43' },
        { value: 'OPC53', label: 'OPC53' },
        { value: 'PSC', label: 'PSC' },
        { value: 'PPC', label: 'PPC' }
      ]
    }
  },
  [CATEGORIES.STEEL]: {
    options: {
      steelType: [
        { value: 'TMT Bars', label: 'TMT Bars (Thermo Mechanically Treated)' },
        { value: 'Mild Steel', label: 'Mild Steel (MS Rods / Angles)' },
        { value: 'Structural Steel', label: 'Structural Steel (Beams, Channels, Angles)' }
      ]
    }
  },
  [CATEGORIES.MACHINERY]: {
    options: {
      machineryType: [
        { value: 'JCB', label: 'JCB' },
        { value: 'Tractor', label: 'Tractor' },
        { value: 'Jack', label: 'Jack' },
        { value: 'Crane', label: 'Crane' }
      ]
    }
  },
  [CATEGORIES.FINANCE]: {
    options: {
      financeType: [
        { value: 'Personal Loan', label: 'Personal Loan' },
        { value: 'Home Loan', label: 'Home Loan' },
        { value: 'Invoice Financing', label: 'Invoice Financing' },
        { value: 'Equipment Financing', label: 'Equipment Financing' },
        { value: 'Business Loan for Women', label: 'Business Loan for Women' },
        { value: 'Term Loan', label: 'Term Loan' }
      ]
    }
  }
};

// Helper functions
const getIndianTimestamp = () => {
  const now = new Date();
  const istOffset = 5.5 * 60 * 60 * 1000; // IST is UTC+5:30
  const istTime = new Date(now.getTime() + istOffset);
  return istTime.toISOString().replace("T", " ").replace("Z", "");
};

const sendNotification = async (userId, queryId, category, brand, companyName) => {
  const db = getDatabase();
  const notificationRef = ref(db, `supplier_notifications/New Query/${userId}/${queryId}`);

  const newNotification = {
    category,
    brand,
    title: "New Query",
    message: `A new ${category} query has been sent by ${companyName}. Please review the query details.`,
    gifUrl,
    timestamp: getIndianTimestamp(),
    seen: false,
  };

  try {
    await set(notificationRef, newNotification);
  } catch (error) {
    console.error("Error sending notification:", error);
  }
};

const getStatusIcon = (status) => {
  const icons = {
    "Pending": <FaClock className="status-icon pending" />,
    "In Progress": <FaPaperPlane className="status-icon in-progress" />,
    "Resolved": <FaCheckCircle className="status-icon resolved" />,
    "default": <FaTimesCircle className="status-icon error" />
  };
  return icons[status] || icons.default;
};

const NewQuery = ({ latestQuery }) => {
  const [category, setCategory] = useState(CATEGORIES.CEMENT);
  const [formData, setFormData] = useState({
    category: '',
    brand: '',
    grade: '',
    steelType: '',
    machineryType: '',
    financeType: '',
    requiredQuantity: '',
    requirementAmount: '',
    contactNumber: '',
    query: '',
    siteAddress: '',
    timestamp: getIndianTimestamp(),
  });

  const [alertState, setAlertState] = useState({ message: '', variant: '', show: false });
  const [currentUser, setCurrentUser] = useState(null);
  const [success, setSuccess] = useState(false);
  const [queries, setQueries] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true); // For loading state
  const [sessionError, setSessionError] = useState(""); // For session/offline errors


  const auth = getAuth();


  useEffect(() => {
    const connectedRef = dbRef(getDatabase(), ".info/connected");

    if (!navigator.onLine) {
      setSessionError("You are offline. Please check your internet connection.");
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        setSessionError("Session expired. Please login again.");
        setLoading(false);
        return;
      }
      setSessionError("");

      setCurrentUser(user);
      if (!latestQuery) {
        setFormData((prev) => ({ ...prev, userId: user.uid }));
      }

      // Firebase connected check
      onValue(connectedRef, (snap) => {
        if (snap.val() === false) {
          setSessionError("Session expired. Please login again.");
          setLoading(false);
          return;
        }
        setSessionError("");
        setLoading(false); // Done checking
      });
    });

    return () => unsubscribe();
  }, [auth, latestQuery]);



  // Set form data from latest query if available
  useEffect(() => {
    if (latestQuery) {
      setFormData(latestQuery);
      if (latestQuery.category) {
        setCategory(latestQuery.category);
      }
    }
  }, [latestQuery]);

  // Fetch queries when user changes or after successful submission
  useEffect(() => {
    if (currentUser) {
      const db = getDatabase();
      const userQueriesRef = ref(db, `queries/${currentUser.uid}`);

      // Subscribe to real-time updates
      const unsubscribe = onValue(userQueriesRef, (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val();
          const queryList = Object.keys(data).map(id => ({
            id,
            ...data[id]
          }));
          setQueries(queryList);
        } else {
          setQueries([]);
        }
      }, (error) => {
        console.error("Error listening to queries:", error);
        setAlertState({
          message: 'Failed to load queries. Please try again.',
          variant: 'danger',
          show: true
        });
      });

      // Cleanup listener on unmount
      return () => unsubscribe();
    }
  }, [currentUser]);

  const handleCategoryChange = (selectedCategory) => {
    setCategory(selectedCategory);
    setFormData(prev => ({
      ...prev,
      category: selectedCategory,
      // Reset category-specific fields
      brand: '',
      grade: '',
      steelType: '',
      machineryType: '',
      financeType: '',
      requiredQuantity: '',
      requirementAmount: ''
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newErrors = { ...errors };

    // Field validations
    const validations = {
      siteAddress: () => {
        if (value.length > 200) return "Site Address cannot exceed 200 characters.";
        if (value.length < 5 && value.length > 0) return "Site Address must be at least 5 characters.";
        return "";
      },
      query: () => {
        if (value.length > 200) return "Query cannot exceed 200 characters.";
        if (value.length < 10 && value.length > 0) return "Query must be at least 10 characters.";
        return "";
      },
      contactNumber: () => {
        const phoneRegex = /^\d{10,15}$/;
        if (!phoneRegex.test(value) && value.length > 0) {
          return "Contact number must be 10-15 digits.";
        }
        return "";
      }

    };

    if (validations[name]) {
      newErrors[name] = validations[name]();
    }

    setFormData({ ...formData, [name]: value });
    setErrors(newErrors);
  };

  const validateForm = () => {
    let formErrors = {};

    if (!formData.contactNumber || formData.contactNumber.length !== 10) {
      formErrors.contactNumber = "Valid contact number is required.";
    }
    if (!formData.siteAddress || formData.siteAddress.length < 5) {
      formErrors.siteAddress = "Site address is required (min 5 characters).";
    }
    if (!formData.query || formData.query.length < 10) {
      formErrors.query = "Query is required (min 10 characters).";
    }

    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!currentUser) {
      setAlertState({
        message: 'User  not logged in.',
        variant: 'danger',
        show: true
      });
      return;
    }

    if (!validateForm()) return;

    try {
      const db = getDatabase();
      const userId = currentUser.uid;
      const profileRef = ref(db, `profileDashboard/${userId}`);

      // Fetch the company name
      const snapshot = await get(profileRef);
      let companyName = snapshot.exists() ? snapshot.val().company_name || 'Not Specified' : 'Not Specified';

      // Prepare the data to be stored based on the selected category
      let queryData = {
        userId,
        companyName,
        submittedAt: getIndianTimestamp(),
        timestamp: getIndianTimestamp(),
        contactNumber: formData.contactNumber,
        siteAddress: formData.siteAddress,
        query: formData.query,
      };

      // Add category-specific fields
      switch (category) {
        case CATEGORIES.CEMENT:
          queryData.category = CATEGORIES.CEMENT;
          queryData.brand = formData.brand;
          queryData.grade = formData.grade;
          break;
        case CATEGORIES.STEEL:
          queryData.category = CATEGORIES.STEEL;
          queryData.steelType = formData.steelType;
          queryData.requiredQuantity = formData.requiredQuantity;
          break;
        case CATEGORIES.MACHINERY:
          queryData.category = CATEGORIES.MACHINERY;
          queryData.machineryType = formData.machineryType;
          break;
        case CATEGORIES.FINANCE:
          queryData.category = CATEGORIES.FINANCE;
          queryData.financeType = formData.financeType;
          queryData.requirementAmount = formData.requirementAmount;
          break;
        default:
          break;
      }

      // Push the query data to Firebase
      const userQueriesRef = ref(db, `queries/${userId}`);
      const newQueryRef = await push(userQueriesRef, queryData);

      // Send notification
      await sendNotification(userId, newQueryRef.key, category, queryData.brand || queryData.steelType || queryData.machineryType || queryData.financeType, companyName);

      // Reset form and show success message
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);

      setFormData({
        category: '',
        brand: '',
        grade: '',
        steelType: '',
        machineryType: '',
        financeType: '',
        requiredQuantity: '',
        requirementAmount: '',
        contactNumber: '',
        query: '',
        siteAddress: '',
        timestamp: getIndianTimestamp()
      });
      setCategory('');

    } catch (error) {
      console.error("Error submitting query:", error);
      setAlertState({
        message: 'Failed to submit query. Please try again.',
        variant: 'danger',
        show: true
      });
    }
  };

  if (loading) {
    return <div className="loading-container"><div className="loader"></div></div>;
  }

  if (sessionError) {
    return <div className="error">{sessionError}</div>;
  }


  // Render select field with options
  const renderSelect = (name, options, label, required = true) => (
    <div className="query-group">
      <label className='query-label'>{label}</label>
      <select
        name={name}
        value={formData[name]}
        onChange={handleChange}
        required={required}
        className='select-input'
      >
        <option value=''>Select {label} ▼</option>
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );

  // Render category-specific fields
  const renderCategoryFields = () => {
    switch (category) {
      case CATEGORIES.CEMENT:
        return (
          <>
            {renderSelect('brand', categoryFields[CATEGORIES.CEMENT].options.brand, 'Brand')}
            {renderSelect('grade', categoryFields[CATEGORIES.CEMENT].options.grade, 'Grade')}
          </>
        );

      case CATEGORIES.STEEL:
        return (
          <>
            {renderSelect('steelType', categoryFields[CATEGORIES.STEEL].options.steelType, 'Steel Type')}
            <div className="query-group">
              <label className='query-label'>Required Quantity</label>
              <input
                type="text"
                name="requiredQuantity"
                value={formData.requiredQuantity}
                onChange={handleChange}
                placeholder="Enter required quantity"
                className="custom-input"
                required
              />
            </div>
          </>
        );

      case CATEGORIES.MACHINERY:
        return renderSelect('machineryType', categoryFields[CATEGORIES.MACHINERY].options.machineryType, 'Machinery Type');

      case CATEGORIES.FINANCE:
        return (
          <>
            {renderSelect('financeType', categoryFields[CATEGORIES.FINANCE].options.financeType, 'Finance Type')}
            <div className="query-group">
              <label className='query-label'>Requirement Amount</label>
              <input
                type="text"
                name="requirementAmount"
                value={formData.requirementAmount}
                onChange={handleChange}
                placeholder="Enter requirement amount"
                className="custom-input"
                required
              />
            </div>
          </>
        );

      default:
        return null;
    }
  };

  // Render query card based on category
  const renderQueryCard = (q) => {
    // Determine status dynamically
    let status = "Pending";
    if (q.supplierReply?.responseMessage) status = "Resolved";
    else if (q.supplierReply?.seen) status = "In Progress";

    const qCategory = q.category || CATEGORIES.CEMENT;

    return (
      <div key={q.id} className="query-card">
        <div className="query-header">
          <span className="query-status">{getStatusIcon(status)} {status}</span>
          <span className="query-time">{q.submittedAt || q.timestamp}</span>
        </div>
        <div className="query-category">{qCategory}</div>

        {/* Category-specific fields */}
        {(qCategory === CATEGORIES.CEMENT || !qCategory) && (
          <>
            <p><strong>Brand:</strong> {q.brand}</p>
            <p><strong>Grade:</strong> {q.grade}</p>
          </>
        )}

        {qCategory === CATEGORIES.STEEL && (
          <>
            <p><strong>Steel Type:</strong> {q.steelType}</p>
            <p><strong>Required Quantity:</strong> {q.requiredQuantity}</p>
          </>
        )}

        {qCategory === CATEGORIES.MACHINERY && (
          <p><strong>Machinery Type:</strong> {q.machineryType}</p>
        )}

        {qCategory === CATEGORIES.FINANCE && (
          <>
            <p><strong>Finance Type:</strong> {q.financeType}</p>
            <p><strong>Requirement Amount:</strong> {q.requirementAmount}</p>
          </>
        )}

        {/* Common fields */}
        <p><strong>Contact:</strong> {q.contactNumber || 'Not provided'}</p>
        <p><strong>Site Address:</strong> {q.siteAddress}</p>
        <p><strong>Query:</strong> {q.query}</p>

        {q.supplierReply?.responseMessage ? (
          <p><strong>Supplier Response:</strong><span className="supplier-response">{q.supplierReply.responseMessage}</span></p>
        ) : (
          <p className="no-response">No response yet from suppliers.</p>
        )}
      </div>
    );
  };

  return (
    <div className="new-query-container">
      {/* Left: Query Form */}
      <div className="query-form-container">
        <h2>Submit a New Query <FaInfoCircle style={{ cursor: 'pointer', fontSize: '1.2rem', color: '#EA8716' }} onClick={() => alert("Kindly put your query to get the best rates for your sites. Please ensure the site address includes the town, district, and state for accurate responses.")} /></h2>

        <form onSubmit={handleSubmit}>
          {/* Category Toggle Selection */}
          <div className="category-toggle">
            {Object.values(CATEGORIES).map(cat => (
              <button
                key={cat}
                type="button"
                className={`toggle-btn ${category === cat ? 'active' : ''}`}
                onClick={() => handleCategoryChange(cat)}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Render category-specific fields */}
          {category && renderCategoryFields()}

          {/* Common fields for all categories */}
          <div className="query-group">
            <label className='query-label'>Contact Number</label>
            <div className="input-with-icon">
              <FaPhone className="input-icon" />
              <input
                type="tel"
                name="contactNumber"
                value={formData.contactNumber}
                onChange={handleChange}
                placeholder="Enter your contact number"
                minLength={10}
                maxLength={15}
                className="custom-input with-icon"
                required
              />
            </div>
            {errors.contactNumber && <p className="error-message">{errors.contactNumber}</p>}
          </div>

          <div className="query-group">
            <label className='query-label'>Site Address</label>
            <input
              type="text"
              name="siteAddress"
              value={formData.siteAddress}
              onChange={handleChange}
              required
              maxLength={200}
              className="custom-input"
              placeholder="Enter site address (town, district, state)"
            />
            {errors.siteAddress && <p className="error-message">{errors.siteAddress}</p>}
            <p className="char-count">{formData.siteAddress.replace(/\s/g, "").length} / 200</p>
          </div>

          <div className="query-group q1-Query-group">
            <label className='query-label'>Query</label>
            <textarea
              name="query"
              value={formData.query}
              onChange={handleChange}
              required
              minLength={10}
              maxLength={200}
              placeholder="Enter your query (10-200 characters)"
              rows={4}
              className="custom-textarea"
            ></textarea>
            {errors.query && <p className="error-message">{errors.query}</p>}
            <p className="char-count">{formData.query.replace(/\s/g, "").length} / 200</p>
          </div>
          <button
            type="submit"
            className="custom-submit-btn"
            disabled={!category}
          >
            Submit Query
          </button>
          {success && <div className="success-Message text-white" >Query submitted successfully!</div>}

          <div className="contact-link">
            <p>Need more help? <Link to="/dashboard/contact-us">Contact Us</Link></p>
          </div>
        </form>
      </div>

      {/* Right: Query Responses */}
      <div className="query-responses-container">
        <h2>Query Responses</h2>

        {queries.length === 0 ? (
          <p className="no-queries">No queries submitted yet.</p>
        ) : (
          [...queries]
            .sort((a, b) => {
              // Determine status priority (lower number = higher priority)
              const getStatusPriority = (query) => {
                if (query.supplierReply?.responseMessage) return 1; // Resolved (highest priority)
                if (query.supplierReply?.seen) return 2; // In Progress
                return 3; // Pending (lowest priority)
              };

              const priorityA = getStatusPriority(a);
              const priorityB = getStatusPriority(b);

              // First, sort by status priority
              if (priorityA !== priorityB) {
                return priorityA - priorityB;
              }

              // If same status, sort by latest submitted date
              return new Date(b.submittedAt || b.timestamp) - new Date(a.submittedAt || a.timestamp);
            })
            .map(renderQueryCard)
        )}
      </div>
    </div>
  );
};

export default NewQuery;
